import React, { useState, useEffect } from 'react';
import { User, LogOut, Sun, Moon, Loader2, MessageSquare, X, Menu } from 'lucide-react';

import { api, API_BASE } from './api/db';
import DashboardView from './views/DashboardView';
import ScreenerView from './views/ScreenerView';
import BondsView from './views/BondsView';
import NewsView from './views/NewsView';
import SimulatorView from './views/SimulatorView';
import PortfolioSimulatorView from './views/PortfolioSimulatorView'; 
import EducationView from './views/EducationView';
import AdminView from './views/AdminView';
import ChatAssistant from './components/ChatAssistant';
import NotesView from './views/NotesView';
import OverlapView from './views/OverlapView';

const MARKET_INDICES = [
  { ticker: "SPY", name: "S&P 500" }, { ticker: "QQQ", name: "NASDAQ" },
  { ticker: "DIA", name: "DOW JONES" }, { ticker: "IWM", name: "RUSSELL 2000" },
  { ticker: "USFR", name: "B500" }, { ticker: "IEF", name: "US 10 YR" }
];

export default function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [currentRoute, setCurrentRoute] = useState('dashboard');
  const [theme, setTheme] = useState('dark');
  const [dailyTip, setDailyTip] = useState("Loading your daily financial insight...");
  const [isTipExpanded, setIsTipExpanded] = useState(false);
  const [isChatPanelOpen, setIsChatPanelOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const [authForm, setAuthForm] = useState({ username: '', password: '', isLogin: true });
  const [authError, setAuthError] = useState("");
  const [isLoadingData, setIsLoadingData] = useState(false);

  const [portfolio, setPortfolio] = useState([]);
  const [notes, setNotes] = useState([]);
  const [watchlist, setWatchlist] = useState(['AAPL', 'MSFT', 'O']);
  const [marketData, setMarketData] = useState({});
  const [indexData, setIndexData] = useState({});

  const [globalSearchTicker, setGlobalSearchTicker] = useState("");
  const [simTicker, setSimTicker] = useState("");

  // Sync Portfolio
  useEffect(() => { 
    if (currentUser && !isLoadingData) {
      const timer = setTimeout(() => api.syncUserData(currentUser.id, { portfolio }), 500);
      return () => clearTimeout(timer);
    }
  }, [portfolio, currentUser, isLoadingData]);

  // Sync Notes
  useEffect(() => { 
    if (currentUser && !isLoadingData) {
      const timer = setTimeout(() => api.syncUserData(currentUser.id, { notes }), 500);
      return () => clearTimeout(timer);
    }
  }, [notes, currentUser, isLoadingData]);

  // Sync Watchlist
  useEffect(() => { 
    if (currentUser && !isLoadingData) {
      const timer = setTimeout(() => api.syncUserData(currentUser.id, { watchlist }), 500);
      return () => clearTimeout(timer);
    }
  }, [watchlist, currentUser, isLoadingData]);

  useEffect(() => {
    if (currentUser) {
      fetch(`${API_BASE}/gemini/chat`, {
        method: 'POST', 
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          contents: [{ parts: [{ text: "Provide a single, insightful 1-sentence tip about investing or risk management." }] }] 
        })
      })
      .then(r => r.json())
      .then(d => {
        if (d.candidates && d.candidates[0]) {
           setDailyTip(d.candidates[0].content.parts[0].text.replace(/[*_]/g, ''));
        }
      })
      .catch(() => setDailyTip("Diversification is the only free lunch in investing."));
      
      // Auto-open chat on desktop only
      if (window.innerWidth >= 768) {
        setIsChatPanelOpen(true);
      }
    }
  }, [currentUser]);

  const handleAuth = async (e) => {
    e.preventDefault();
    setAuthError("");
    setIsLoadingData(true);
    try {
      let user;
      if (authForm.isLogin) { user = await api.login(authForm.username, authForm.password); } 
      else { user = await api.register(authForm.username, authForm.password); }
      
      const dbData = await api.getUserData(user.id);
      setPortfolio(dbData.portfolio || []);
      setNotes(dbData.notes || []);
      setWatchlist(dbData.watchlist || ['AAPL', 'MSFT', 'O']); 
      
      setCurrentUser(user);
    } catch (err) { setAuthError(err.message); } finally { setIsLoadingData(false); }
  };

  const handleLogout = () => {
    setCurrentUser(null);
    setPortfolio([]);
    setNotes([]);
    setWatchlist(['AAPL', 'MSFT', 'O']);
    setCurrentRoute('dashboard');
    setIsMobileMenuOpen(false);
  };

  const navigateTo = (route) => {
    setCurrentRoute(route);
    setIsMobileMenuOpen(false);
  };

  if (!currentUser) {
    return (
      <div className="min-h-screen bg-[#050608] flex items-center justify-center text-white font-sans selection:bg-indigo-500/30 p-4">
        <div className="bg-[#0f1115] p-6 sm:p-10 rounded-3xl border border-slate-800 shadow-2xl w-full max-w-md">
          <div className="text-4xl font-black italic tracking-tighter text-indigo-500 mb-8 text-center drop-shadow-[0_0_15px_rgba(99,102,241,0.5)]">vInvest</div>
          <form onSubmit={handleAuth} className="space-y-6">
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2 block">Username</label>
              <input required type="text" value={authForm.username} onChange={e => setAuthForm({...authForm, username: e.target.value})} className="w-full bg-[#0a0c10] border border-slate-800 rounded-xl p-4 text-sm outline-none focus:border-indigo-500 transition-colors" />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2 block">Password</label>
              <input required type="password" value={authForm.password} onChange={e => setAuthForm({...authForm, password: e.target.value})} className="w-full bg-[#0a0c10] border border-slate-800 rounded-xl p-4 text-sm outline-none focus:border-indigo-500 transition-colors" />
            </div>
            {authError && <p className="text-red-500 text-xs font-bold text-center">{authError}</p>}
            <button type="submit" disabled={isLoadingData} className="w-full py-4 bg-indigo-600 rounded-xl text-white font-black uppercase tracking-widest text-xs hover:bg-indigo-500 transition-colors shadow-lg shadow-indigo-500/20 disabled:opacity-50">
              {isLoadingData ? <Loader2 className="animate-spin mx-auto" size={16}/> : (authForm.isLogin ? 'Access Terminal' : 'Create Account')}
            </button>
          </form>
          <p className="mt-6 text-center text-xs text-slate-500 font-medium">
            {authForm.isLogin ? "Don't have an account? " : "Already registered? "}
            <button onClick={() => setAuthForm({...authForm, isLogin: !authForm.isLogin, password: ''})} className="text-indigo-400 hover:text-indigo-300 font-bold">{authForm.isLogin ? "Sign Up" : "Log In"}</button>
          </p>
        </div>
      </div>
    );
  }

  const themeClasses = theme === 'dark' 
    ? { bgApp: 'bg-[#050608]', bgCard: 'bg-[#0f1115]', border: 'border-slate-800', textMain: 'text-slate-300', textHead: 'text-white', textMuted: 'text-slate-400', input: 'bg-[#0a0c10]' }
    : { bgApp: 'bg-slate-100', bgCard: 'bg-white', border: 'border-slate-200', textMain: 'text-slate-600', textHead: 'text-slate-900', textMuted: 'text-slate-500', input: 'bg-slate-50' };

  return (
    <div className={`h-screen overflow-hidden ${themeClasses.bgApp} ${themeClasses.textMain} font-sans flex flex-col transition-colors duration-300 relative`}>
      
      {/* Top Navigation Bar */}
      <nav className={`${themeClasses.bgCard} border-b ${themeClasses.border} px-4 md:px-6 py-4 flex justify-between items-center shrink-0 z-50 shadow-sm relative`}>
        <div className="flex items-center gap-4 md:gap-10">
          <button 
            className={`md:hidden p-1.5 rounded-lg border ${themeClasses.border} ${themeClasses.textMuted} hover:text-indigo-500 focus:outline-none`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
          
          <div className="text-xl md:text-2xl font-black italic tracking-tighter text-indigo-500 drop-shadow-[0_0_10px_rgba(99,102,241,0.3)] cursor-pointer" onClick={() => navigateTo('dashboard')}>vInvest</div>
          
          {/* Desktop Navigation Links */}
          <div className={`hidden md:flex gap-6 text-[11px] font-black uppercase tracking-widest ${themeClasses.textMuted}`}>
            <button onClick={() => navigateTo('dashboard')} className={`${currentRoute === 'dashboard' ? 'text-indigo-500' : 'hover:text-indigo-400'} transition-colors`}>Dashboard</button>
            <button onClick={() => navigateTo('screener')} className={`${currentRoute === 'screener' ? 'text-indigo-500' : 'hover:text-indigo-400'} transition-colors`}>Screener</button>
            <button onClick={() => navigateTo('overlap')} className={`${currentRoute === 'overlap' ? 'text-indigo-500' : 'hover:text-indigo-400'} transition-colors`}>Overlap</button>
            <button onClick={() => navigateTo('bonds')} className={`${currentRoute === 'bonds' ? 'text-indigo-500' : 'hover:text-indigo-400'} transition-colors`}>Fixed Income</button>
            <button onClick={() => navigateTo('news')} className={`${currentRoute === 'news' ? 'text-indigo-500' : 'hover:text-indigo-400'} transition-colors`}>News</button>
            <button onClick={() => navigateTo('portfolio-simulator')} className={`${currentRoute === 'portfolio-simulator' ? 'text-indigo-500' : 'hover:text-indigo-400'} transition-colors`}>Sandbox</button>
            <button onClick={() => navigateTo('simulator')} className={`${currentRoute === 'simulator' ? 'text-indigo-500' : 'hover:text-indigo-400'} transition-colors`}>Strategies</button>
            <button onClick={() => navigateTo('education')} className={`${currentRoute === 'education' ? 'text-indigo-500' : 'hover:text-indigo-400'} transition-colors`}>Education</button>
            <button onClick={() => navigateTo('notes')} className={`${currentRoute === 'notes' ? 'text-indigo-500' : 'hover:text-indigo-400'} transition-colors`}>Notes</button>
            {currentUser.role === 'admin' && (
              <button onClick={() => navigateTo('admin')} className={`${currentRoute === 'admin' ? 'text-indigo-500' : 'hover:text-indigo-400'} transition-colors`}>Admin</button>
            )}
          </div>
        </div>

        <div className="flex items-center gap-4 md:gap-6">
          {/* Daily Tip (Hidden on mobile) */}
          <div className="relative hidden xl:block">
            <div onClick={() => setIsTipExpanded(!isTipExpanded)} className={`bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 text-[10px] font-bold px-4 py-2 rounded-full max-w-xs xl:max-w-md cursor-pointer hover:bg-indigo-500/20 transition-all select-none ${isTipExpanded ? 'ring-2 ring-indigo-500/50' : 'truncate'}`}>
               💡 <span className="font-black uppercase tracking-widest mr-1">Daily Tip:</span> {dailyTip}
            </div>
            {isTipExpanded && (
              <div className={`absolute top-full right-0 xl:left-0 mt-3 w-[350px] md:w-[450px] ${themeClasses.bgCard} border border-indigo-500/30 p-5 rounded-2xl shadow-2xl z-[100] text-indigo-500 text-xs leading-relaxed`}>
                <div className="font-black uppercase tracking-widest text-indigo-500 mb-2 flex justify-between items-center">
                  <span>💡 Daily Insight</span>
                  <button onClick={(e) => { e.stopPropagation(); setIsTipExpanded(false); }} className="hover:text-red-400 transition-colors"><X size={14}/></button>
                </div>
                <span className={themeClasses.textMain}>{dailyTip}</span>
              </div>
            )}
          </div>
          
          {/* User Menu Dropdown */}
          <div className="relative group">
            <button className={`flex items-center gap-2 ${themeClasses.bgApp} border ${themeClasses.border} px-3 md:px-4 py-2 rounded-xl hover:border-indigo-500 transition-colors`}>
              <User size={14} className="text-indigo-500"/>
              <span className={`text-[11px] font-black uppercase tracking-widest ${themeClasses.textHead} truncate max-w-[80px] md:max-w-none`}>{currentUser.username}</span>
            </button>
            
            <div className="absolute right-0 top-full pt-2 w-48 opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto transition-all z-[100]">
              <div className={`${themeClasses.bgCard} border ${themeClasses.border} rounded-xl shadow-xl overflow-hidden flex flex-col`}>
                 <button onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')} className={`flex items-center gap-3 px-4 py-3 text-xs font-bold hover:bg-indigo-500/10 hover:text-indigo-500 transition-colors border-b ${themeClasses.border} ${themeClasses.textHead}`}>
                   {theme === 'dark' ? <Sun size={14}/> : <Moon size={14}/>} {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
                 </button>
                 <button onClick={handleLogout} className="flex items-center gap-3 px-4 py-3 text-xs font-bold text-red-500 hover:bg-red-500/10 transition-colors">
                   <LogOut size={14}/> Secure Logout
                 </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Dropdown Navigation Menu */}
      {isMobileMenuOpen && (
        <div className={`md:hidden absolute top-[65px] left-0 w-full ${themeClasses.bgCard} border-b ${themeClasses.border} z-[45] flex flex-col py-2 shadow-2xl`}>
          {[
            { id: 'dashboard', label: 'Dashboard' },
            { id: 'screener', label: 'Screener' },
            { id: 'overlap', label: 'Overlap' },
            { id: 'bonds', label: 'Fixed Income' },
            { id: 'news', label: 'News' },
            { id: 'portfolio-simulator', label: 'Sandbox' },
            { id: 'simulator', label: 'Strategies' },
            { id: 'education', label: 'Education' },
            { id: 'notes', label: 'Notes' }
          ].map(route => (
             <button 
                key={route.id}
                onClick={() => navigateTo(route.id)} 
                className={`text-left px-6 py-4 text-xs font-black uppercase tracking-widest border-b border-transparent ${currentRoute === route.id ? 'bg-indigo-500/10 text-indigo-500' : `${themeClasses.textMuted} hover:bg-slate-800/30 hover:text-indigo-400`}`}
             >
                {route.label}
             </button>
          ))}
          {currentUser.role === 'admin' && (
            <button onClick={() => navigateTo('admin')} className={`text-left px-6 py-4 text-xs font-black uppercase tracking-widest ${currentRoute === 'admin' ? 'bg-indigo-500/10 text-indigo-500' : `${themeClasses.textMuted} hover:bg-slate-800/30 hover:text-indigo-400`}`}>Admin</button>
          )}
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex-1 flex overflow-hidden relative">
        <div className={`flex-1 overflow-y-auto custom-scrollbar px-4 sm:px-6 pt-6 pb-20 md:pb-6`}>
           <div className={currentRoute === 'dashboard' ? 'block h-full' : 'hidden'}>
             <DashboardView 
               setCurrentRoute={setCurrentRoute} 
               portfolio={portfolio} 
               setPortfolio={setPortfolio} 
               watchlist={watchlist} 
               setWatchlist={setWatchlist} 
               marketData={marketData} 
               setMarketData={setMarketData} 
               indexData={indexData} 
               setIndexData={setIndexData} 
               notes={notes} 
               setNotes={setNotes} 
               globalSearchTicker={globalSearchTicker} 
               setGlobalSearchTicker={setGlobalSearchTicker} 
               setSimTicker={setSimTicker} 
               themeClasses={themeClasses}
             />
           </div>
           <div className={currentRoute === 'screener' ? 'block h-full' : 'hidden'}>
             <ScreenerView setCurrentRoute={setCurrentRoute} setGlobalSearchTicker={setGlobalSearchTicker} themeClasses={themeClasses} />
           </div>
           <div className={currentRoute === 'overlap' ? 'block h-full' : 'hidden'}>
              <OverlapView themeClasses={themeClasses} portfolio={portfolio} />
            </div>
           <div className={currentRoute === 'bonds' ? 'block h-full' : 'hidden'}>
              <BondsView setCurrentRoute={setCurrentRoute} setGlobalSearchTicker={setGlobalSearchTicker} themeClasses={themeClasses} />
            </div>
           <div className={currentRoute === 'news' ? 'block h-full' : 'hidden'}>
             <NewsView themeClasses={themeClasses} />
           </div>
           <div className={currentRoute === 'portfolio-simulator' ? 'block h-full' : 'hidden'}>
             <PortfolioSimulatorView themeClasses={themeClasses} currentUser={currentUser} />
           </div>
           <div className={currentRoute === 'simulator' ? 'block h-full' : 'hidden'}>
             <SimulatorView simTicker={simTicker} setSimTicker={setSimTicker} themeClasses={themeClasses} />
           </div>
           <div className={currentRoute === 'education' ? 'block h-full' : 'hidden'}>
             <EducationView themeClasses={themeClasses} />
           </div>
           <div className={currentRoute === 'notes' ? 'block h-full' : 'hidden'}>
             <NotesView notes={notes} setNotes={setNotes} themeClasses={themeClasses} />
           </div>
           {currentUser.role === 'admin' && (
             <div className={currentRoute === 'admin' ? 'block h-full' : 'hidden'}>
               <AdminView themeClasses={themeClasses} />
             </div>
           )}
        </div>
        
        {/* Chat Assistant (Responsive Side Panel or Full Overlay) */}
        {isChatPanelOpen && (
          <div className={`absolute inset-0 md:relative w-full md:w-96 border-l ${themeClasses.border} ${themeClasses.bgCard} flex flex-col shrink-0 shadow-2xl z-[60] transition-all duration-300`}>
             <ChatAssistant key={currentUser.username} currentUser={currentUser} portfolio={portfolio} themeClasses={themeClasses} setIsChatPanelOpen={setIsChatPanelOpen} notes={notes} setNotes={setNotes}/>
          </div>
        )}
      </div>

      {/* Floating Chat Button */}
      {!isChatPanelOpen && (
        <button 
          onClick={() => setIsChatPanelOpen(true)} 
          className="fixed bottom-6 right-6 md:bottom-16 md:right-8 z-[60] bg-indigo-600 p-4 rounded-full text-white shadow-[0_10px_30px_rgba(99,102,241,0.4)] hover:bg-indigo-500 hover:scale-110 active:scale-95 transition-all flex items-center justify-center"
        >
          <MessageSquare size={24} />
        </button>
      )}
    </div>
  );
}